import { KROT } from 'captcha-eu-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FilePond } from 'react-filepond';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

const CAPTCHA_PUBLIC_KEY = import.meta.env.VITE_CAPTCHA_PUBLIC_KEY;
const POLL_INTERVAL_MS = 2000;
const POLL_START_DELAY_MS = 3000;

/** Minimal shape shared by the employee-list and current-policy status endpoints. */
export interface ExtractionStatusLike<TResult> {
	status: string;
	result?: TResult;
}

/** Helpers handed to `onCompleted` so callers can finish/branch without re-deriving setters. */
export interface ExtractionUploadHelpers {
	setWarning: (message: string | null) => void;
	setError: (message: string | null) => void;
	clearFiles: () => void;
	stopProcessing: () => void;
}

interface UseExtractionUploadOptions<TResult> {
	upload: (id: string, file: File, captchaSolution: string) => Promise<unknown>;
	/**
	 * Status endpoint to poll after upload. Omit when the caller drives completion
	 * by other means (e.g. the policy upload locks the form and lets the quotes
	 * view's own app-state polling surface the result) — polling is then skipped.
	 */
	fetchStatus?: (id: string) => Promise<ExtractionStatusLike<TResult>>;
	onCompleted?: (
		result: TResult | undefined,
		helpers: ExtractionUploadHelpers
	) => void | Promise<void>;
	/**
	 * Called once the upload POST succeeds, before status polling begins. Lets a
	 * caller react to the server-side state the upload triggered (e.g. the policy
	 * upload flips the form to PROCESSING_QUOTES, so the quotes view must restart
	 * its own polling to lock the now-stale offer).
	 */
	onUploaded?: () => void;
}

/**
 * Shared FilePond + captcha + upload + status-polling lifecycle for document
 * extraction uploads. Used by the employee-list import and the current-policy
 * PDF upload (issue #961) — the only difference between them is what happens on
 * completion, which is delegated to `onCompleted`.
 */
export const useExtractionUpload = <TResult>({
	upload,
	fetchStatus,
	onCompleted,
	onUploaded
}: UseExtractionUploadOptions<TResult>) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [files, setFiles] = useState<any[]>([]);
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [warning, setWarning] = useState<string | null>(null);
	const [isCaptchaReady, setIsCaptchaReady] = useState(false);
	const filePondRef = useRef<FilePond | null>(null);
	const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const pollStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/**
	 * Generation counter identifying the current upload. Bumped whenever an upload
	 * starts or is torn down, so a request that was already in flight at teardown
	 * can tell it has been superseded once it finally resolves. Clearing timers
	 * alone is not enough: the continuation of an in-flight `upload` or
	 * `fetchStatus` would otherwise schedule fresh polling, or run `onCompleted`
	 * and persist an import, after the user had already cancelled.
	 */
	const runIdRef = useRef(0);

	useEffect(() => {
		KROT.setup(CAPTCHA_PUBLIC_KEY);
		setIsCaptchaReady(true);
	}, []);

	const clearPolling = useCallback(() => {
		// Invalidate the current upload first, so in-flight requests are ignored
		// when they resolve, then clear both the delayed start timer and the running
		// interval — an unmount (or file removal) during the start delay must not
		// kick off polling afterwards.
		runIdRef.current += 1;
		if (pollStartTimeoutRef.current) {
			clearTimeout(pollStartTimeoutRef.current);
			pollStartTimeoutRef.current = null;
		}
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}
	}, []);

	const clearFiles = useCallback(() => {
		filePondRef.current?.removeFiles();
		setFiles([]);
	}, []);

	const stopProcessing = useCallback(() => setIsProcessing(false), []);

	const getCaptchaSolution = useCallback(async (): Promise<string> => {
		if (window.localStorage.getItem('__e2e__')) return '';
		try {
			const solution = await KROT.getSolution();
			return JSON.stringify(solution);
		} catch (err) {
			console.error('Captcha error:', err);
			throw new Error('Failed to get captcha solution', { cause: err });
		}
	}, []);

	const pollExtractionStatus = useCallback(async () => {
		if (!id || !fetchStatus) return;
		const runId = runIdRef.current;
		try {
			const response = await fetchStatus(id);
			// Cancelled while this request was in flight — completing now would
			// import employees the user has already walked away from.
			if (runId !== runIdRef.current) return;
			if (response.status === 'completed') {
				clearPolling();
				await onCompleted?.(response.result, {
					setWarning,
					setError,
					clearFiles,
					stopProcessing
				});
			} else if (response.status === 'failed') {
				clearPolling();
				setError(t('errors.fileProcessingFailed'));
				setIsProcessing(false);
				clearFiles();
			}
			// Any other status (processing / in_progress) keeps polling.
		} catch (err) {
			console.error('Polling error:', err);
			// Keep polling on transient errors.
		}
	}, [id, fetchStatus, onCompleted, clearPolling, clearFiles, stopProcessing, t]);

	const handleProcessFile = useCallback(
		async (
			_fieldName: string,
			file: File,
			_metadata: unknown,
			load: (value: string) => void,
			error: (errorMessage: string) => void
		) => {
			try {
				setIsProcessing(true);
				setError(null);
				setWarning(null);

				if (!id) throw new Error(t('errors.noValidId'));
				if (!isCaptchaReady) throw new Error(t('errors.captchaNotReady'));

				// Supersede any earlier upload, and remember which one this is.
				runIdRef.current += 1;
				const runId = runIdRef.current;

				const captchaSolution = await getCaptchaSolution();
				await upload(id, file, captchaSolution);

				// Upload accepted: the server may have changed form state (e.g. locked
				// the offer to PROCESSING_QUOTES). Report that even if the user has
				// since cancelled — the server-side change happened regardless, and
				// the caller has to reconcile with it.
				onUploaded?.();

				load('success');

				// Cancelled while the upload was in flight: the file is gone from the
				// dropzone, so don't start polling for its result.
				if (runId !== runIdRef.current) return;

				// Poll the extraction status only when a status endpoint is provided;
				// callers that drive completion elsewhere (policy upload) skip this.
				if (fetchStatus) {
					pollStartTimeoutRef.current = setTimeout(() => {
						pollingIntervalRef.current = setInterval(pollExtractionStatus, POLL_INTERVAL_MS);
					}, POLL_START_DELAY_MS);
				}
			} catch (err) {
				console.error('Upload error:', err);
				setError(t('errors.fileUploadFailed'));
				setIsProcessing(false);
				clearFiles();
				error(t('errors.fileUploadFailed'));
			}
		},
		[
			id,
			isCaptchaReady,
			getCaptchaSolution,
			upload,
			onUploaded,
			fetchStatus,
			pollExtractionStatus,
			clearFiles,
			t
		]
	);

	// Cleanup interval on unmount.
	useEffect(() => clearPolling, [clearPolling]);

	return {
		files,
		setFiles,
		isProcessing,
		setIsProcessing,
		error,
		setError,
		warning,
		setWarning,
		filePondRef,
		handleProcessFile,
		clearPolling,
		clearFiles,
		stopProcessing
	};
};
